import React, { useContext, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { AuthUserContext } from '../Session';
import { useActiveRestaurant } from '../../hooks/useActiveRestaurant.js';
import Admin from '../Admin';

// Reusable loading component
const LoadingScreen = ({ message = 'Se încarcă...' }) => (
    <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
    }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
            {message}
        </div>
    </div>
);

// Component to handle homepage redirect based on user role
const HomeRedirect = () => {
    const authUser = useContext(AuthUserContext);
    const { activeRestaurant, hasActiveRestaurant, restaurantId } = useActiveRestaurant();
    
    // Memoize user type check to avoid repeated property access
    const userType = useMemo(() => authUser?.profile?.userType, [authUser?.profile?.userType]);
    
    // Memoize booking module check
    const hasModuleBooking = useMemo(() => {
        return activeRestaurant?.modules?.includes('booking') ?? false;
    }, [activeRestaurant?.modules]);
    // Early return for authentication loading
    if (authUser === undefined) {
        return <LoadingScreen message="Checking authentication..." />;
    }
    
    // Early return for unauthenticated users
    if (authUser === null) {
        return <Navigate replace to="/signin" />;
    }
    
    // Handle non-waiter users immediately
    if (userType !== 'waiter') {
        return <Admin />;
    }
    
    // Handle waiters without restaurant
    if (!hasActiveRestaurant || !restaurantId) {
        return <Admin />; // Will show waiter-restricted sidebar
    }
    
    // Wait for complete restaurant data for waiters
    if (!activeRestaurant?.uniqueIdentificationToken) {
        return <LoadingScreen message="Loading restaurant data..." />;
    }
    
    // Redirect waiter based on available modules
    const redirectPath = hasModuleBooking 
        ? `/admin/locations/${restaurantId}/bookings`
        : `/admin/locations/${restaurantId}/feedback`;
    
    return <Navigate replace to={redirectPath} />;
};

export default HomeRedirect;